<template>
    <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
            <p class="modal-card-title">Redirecting to Quiz Attempt</p>
        </header>

        <section class="modal-card-body">
            <p>{{ quizAttemptStatus }}</p>

            <p v-if="loading" role="status" class="m-t-lg">Loading your quiz attempt...</p>
            <p v-else-if="loadError" role="alert" class="m-t-lg">
                Unable to continue to your quiz. Please try again.
            </p>
            <p v-else class="m-t-lg">You will be <strong>redirected</strong> in {{ countDown }}.</p>
        </section>

        <footer class="modal-card-foot">
            <e-button
                v-if="closing"
                label="Go Now"
                :loading="submitting"
                type="is-primary"
                @click="close"
            />
            <e-button
                v-else-if="loadError"
                label="Try again"
                data-test="retry-quiz-transition"
                :loading="loading || submitting"
                type="is-primary"
                @click="retry"
            />
            <e-button
                v-else
                label="Go Now"
                :loading="submitting"
                type="is-primary"
                :disabled="loading || submitting"
                @click="navigateToQuiz"
            />
        </footer>
    </div>
</template>

<script>
import isNull from 'lodash/isNull.js';
import { useI18n } from 'vue-i18n';

import QuizAttempt from '@/models/QuizAttempt.js';
import User from '@/models/User.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';
import { findQuizAttemptModule } from '@/utilities/synchronizeQuizStatus.js';

export default {
    name: 'QuizAttemptRedirectModal',

    props: {
        status: {
            type: String,
            default: 'started',
        },

        quizAttemptId: {
            type: [Number, String],
            required: false,
            default: null,
        },

        closing: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['close'],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    data: () => ({
        countDown: 10,
        timer: 1000,
        interval: null,
        submitting: false,
        checks: ['in-waiting-room', 'ready-to-start', 'attempt-started', 'in-waiting-room-post'],
        quizAttempt: null,
        loading: false,
        loadError: false,
        disposed: false,
        completed: false,
        lifecycle: 0,
        requestController: null,
    }),

    computed: {
        quizAttemptStatus() {
            return this.started
                ? this.translate('quiz_attempt_started', { status: 'started' })
                : this.translate('quiz_attempt_ended', { status: 'ended' });
        },

        started() {
            return this.checks.some(check => check === this.status);
        },

        transitionKey() {
            return `${this.quizAttemptId}:${this.status}:${this.closing}`;
        },
    },

    watch: {
        transitionKey() {
            this.initialize();
        },
    },

    mounted() {
        this.initialize();
    },

    beforeUnmount() {
        this.disposed = true;
        this.lifecycle += 1;
        this.requestController?.abort();
        this.stopCountdown();
    },

    methods: {
        stopCountdown() {
            clearInterval(this.interval);
            this.interval = null;
        },

        startCountdown() {
            this.stopCountdown();
            if (this.disposed || this.completed || this.loading || this.loadError) return;
            this.countDown = 10;
            this.interval = setInterval(() => {
                this.countDown -= 1;
                if (this.countDown === 0) {
                    if (this.closing) this.close();
                    else this.navigateToQuiz();
                }
            }, this.timer);
        },

        async initialize() {
            const lifecycle = ++this.lifecycle;
            this.requestController?.abort();
            this.stopCountdown();
            this.completed = false;
            this.submitting = false;
            this.loadError = false;
            this.quizAttempt = null;
            if (this.closing) {
                this.loading = false;
                this.startCountdown();
                return;
            }
            if (await this.fetchAttempt(lifecycle)) this.startCountdown();
        },

        async fetchAttempt(lifecycle = this.lifecycle) {
            if (this.disposed) return false;
            const controller = new AbortController();
            this.requestController = controller;
            this.loading = true;
            this.loadError = false;
            try {
                if (!(Number(this.quizAttemptId) > 0)) throw new Error('Missing quiz attempt.');
                await User.update({
                    where: this.$store.state.Auth.user.id,
                    data: { quiz_modal: null },
                });
                if (this.disposed || lifecycle !== this.lifecycle) return false;

                const queryBuilder = new QueryBuilder(`quiz-attempts/${this.quizAttemptId}`);
                queryBuilder.include(['quizActivity.activity', 'surveyActivity.activity']);
                const { response } = await QuizAttempt.api().get(queryBuilder.toUrl(), {
                    timeout: 15000,
                    retry: false,
                    errorHandle: false,
                    signal: controller.signal,
                });
                if (this.disposed || lifecycle !== this.lifecycle) return false;
                if (Number(response.data.data?.id) !== Number(this.quizAttemptId)) {
                    throw new Error('Missing quiz attempt.');
                }
                this.quizAttempt = response.data.data;
                return true;
            } catch {
                if (!this.disposed && lifecycle === this.lifecycle) this.loadError = true;
                return false;
            } finally {
                if (!this.disposed && lifecycle === this.lifecycle) this.loading = false;
            }
        },

        close() {
            if (this.disposed || this.completed || this.submitting) return;
            this.completed = true;
            this.submitting = true;
            this.stopCountdown();
            this.$emit('close');
        },

        async retry() {
            if (this.loading || this.submitting || this.disposed) return;
            const { lifecycle } = this;
            if (await this.fetchAttempt(lifecycle)) await this.navigateToQuiz();
        },

        async navigateToQuiz() {
            if (this.disposed || this.completed || this.loading || this.submitting || this.loadError) return;
            this.submitting = true;
            this.stopCountdown();
            const { lifecycle } = this;
            const isCurrent = () => !this.disposed && lifecycle === this.lifecycle;

            try {
                if (!this.quizAttempt) throw new Error('Missing quiz attempt.');
                const moduleId = findQuizAttemptModule(this.$store, this.quizAttempt.id);
                if (this.started && moduleId !== null && this.status === 'attempt-started') {
                    await this.$store.dispatch(`QuizAttempt/${moduleId}/closeModal`);
                    if (!isCurrent()) return;
                    const hydrated = await this.$store.dispatch(`QuizAttempt/${moduleId}/hydrateQuizAttempt`, {
                        quiz_attempt: this.quizAttempt,
                        isCurrent,
                    });
                    if (!isCurrent()) return;
                    if (hydrated === false) throw new Error('Unable to load the quiz.');
                    this.completed = true;
                    this.$emit('close');
                    return;
                }

                const activity = this.quizAttempt.quiz_activity?.activity ?? this.quizAttempt.survey_activity?.activity;
                if (!activity?.id || !activity.course_id) throw new Error('Missing quiz activity.');
                let target;
                if (!isNull(this.quizAttempt.end_time) && !this.status) {
                    if (moduleId !== null) {
                        const ended = await this.$store.dispatch(`QuizAttempt/${moduleId}/endAndApplyStatus`, {
                            status: null,
                            isCurrent,
                        });
                        if (!isCurrent()) return;
                        if (ended === false) throw new Error('Unable to complete the quiz transition.');
                    }
                    target = {
                        name: 'activity-view',
                        params: { activityId: activity.id, courseId: activity.course_id },
                    };
                } else {
                    const survey = !this.quizAttempt.quiz_activity;
                    target = {
                        name: survey ? 'survey-attempt' : 'quiz-attempt',
                        params: {
                            activityId: activity.id,
                            courseId: activity.course_id,
                            id: this.quizAttempt.quiz_version_id,
                            attemptId: this.quizAttempt.id,
                        },
                    };
                }

                if (!isCurrent()) return;
                const { href } = this.$router.resolve(target);
                this.completed = true;
                window.location.href = href;
            } catch {
                if (isCurrent()) this.loadError = true;
            } finally {
                if (isCurrent()) this.submitting = false;
            }
        },
    },
};
</script>
